import React, { useState } from 'react';
import { 
  ExternalLink, 
  Copy, 
  Edit2, 
  Star, 
  Trash2, 
  AlertCircle,
  FileText,
  Eye,
  HelpCircle,
  Lock
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';

const docRoleColors = {
  DRAFT: 'bg-blue-50 text-blue-700 border-blue-200',
  PLANNING: 'bg-slate-50 text-slate-700 border-slate-200',
  REFLECTION: 'bg-purple-50 text-purple-700 border-purple-200',
  FEEDBACK: 'bg-amber-50 text-amber-700 border-amber-200',
  OTHER: 'bg-slate-50 text-slate-600 border-slate-200',
  FINAL: 'bg-green-50 text-green-700 border-green-200',
  NOTES: 'bg-slate-50 text-slate-600 border-slate-200',
  OUTLINE: 'bg-violet-50 text-violet-700 border-violet-200',
  BRAINSTORM: 'bg-teal-50 text-teal-700 border-teal-200',
};

const docRoleLabels = {
  DRAFT: 'Draft',
  PLANNING: 'Planning',
  REFLECTION: 'Reflection',
  FEEDBACK: 'Feedback',
  OTHER: 'Other',
  FINAL: 'Final',
  NOTES: 'Notes',
  OUTLINE: 'Outline',
  BRAINSTORM: 'Brainstorm',
};

const GoogleDocsPanel = ({ 
  docs = [], 
  onUpdateDoc, 
  onDeleteDoc, 
  onSetCurrent,
  selectedDocId,
  onSelectDoc
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const selectedDoc = docs.find(d => d.id === selectedDocId) || docs.find(d => d.isCurrent) || docs[0];

  const handleCopyLink = async (doc) => {
    try {
      await navigator.clipboard.writeText(doc.url);
      toast.success('Link copied!');
    } catch (err) {
      toast.info('Copy this link: ' + doc.url);
    }
  };

  const handleRename = (doc) => {
    setEditingId(doc.id);
    setEditTitle(doc.title || '');
  };

  const handleSaveRename = async (doc) => {
    if (editTitle.trim()) {
      await onUpdateDoc(doc.id, { title: editTitle });
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleRoleChange = async (doc, newRole) => {
    await onUpdateDoc(doc.id, { role: newRole });
  };

  // Extract Google Doc ID for embedding
  const getEmbedUrl = (doc) => {
    if (!doc?.url) return null;
    const match = doc.url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (match) {
      return `https://docs.google.com/document/d/${match[1]}/preview`;
    }
    return null;
  };

  if (docs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Docs List */}
      <div className="space-y-2">
        {docs.map((doc) => (
          <div 
            key={doc.id}
            onClick={() => onSelectDoc?.(doc.id)}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              selectedDoc?.id === doc.id 
                ? 'bg-indigo-50 border-indigo-200 ring-1 ring-indigo-200' 
                : 'bg-white border-slate-100 hover:border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {doc.isCurrent && <Star className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="currentColor" />}
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                
                {editingId === doc.id ? (
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={() => handleSaveRename(doc)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveRename(doc)}
                    className="h-7 text-sm"
                    autoFocus
                  />
                ) : (
                  <span className="text-sm font-medium text-slate-900 truncate">
                    {doc.title || 'Untitled Document'}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                <Badge variant="outline" className={`text-xs ${docRoleColors[doc.role] || docRoleColors.OTHER}`}>
                  {docRoleLabels[doc.role] || doc.role}
                </Badge>
              </div>
            </div>

            {/* Actions row */}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={(e) => { e.stopPropagation(); window.open(doc.url, '_blank'); }}
                  data-testid={`open-doc-${doc.id}`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Open in Docs
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={(e) => { e.stopPropagation(); handleCopyLink(doc); }}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => { e.stopPropagation(); handleRename(doc); }}
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
                
                {!doc.isCurrent && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => { e.stopPropagation(); onSetCurrent(doc.id); }}
                    title="Set as current"
                  >
                    <Star className="w-3 h-3" />
                  </Button>
                )}
                
                <Select 
                  value={doc.role} 
                  onValueChange={(v) => { handleRoleChange(doc, v); }}
                >
                  <SelectTrigger className="h-7 w-24 text-xs" onClick={(e) => e.stopPropagation()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(docRoleLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); onDeleteDoc(doc.id); }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Panel */}
      {selectedDoc && (
        <Card className="overflow-hidden">
          <CardHeader className="py-3 px-4 bg-slate-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-slate-500" />
                <CardTitle className="text-sm font-medium">Preview (view-only)</CardTitle>
              </div>
              <Button
                size="sm"
                onClick={() => window.open(selectedDoc.url, '_blank')}
                data-testid="preview-open-in-docs"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Google Docs
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <GoogleDocEmbed doc={selectedDoc} embedUrl={getEmbedUrl(selectedDoc)} />
          </CardContent>
        </Card>
      )}

      {/* Privacy Note */}
      <p className="text-xs text-slate-500 flex items-center gap-1">
        <Lock className="w-3 h-3" />
        Your document stays in Google Docs. This app only links and previews when allowed.
      </p>
    </div>
  );
};

// Separate embed component with error handling
const GoogleDocEmbed = ({ doc, embedUrl }) => {
  const [embedError, setEmbedError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  if (!embedUrl) {
    return (
      <div className="p-8 text-center bg-slate-50">
        <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="text-sm text-slate-600 mb-4">Unable to generate preview for this document</p>
        <Button onClick={() => window.open(doc.url, '_blank')}>
          <ExternalLink className="w-4 h-4 mr-2" />
          Open in Google Docs
        </Button>
      </div>
    );
  }

  if (embedError) {
    return (
      <div className="p-8 text-center bg-amber-50/50">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
        <p className="text-sm text-slate-700 mb-2 font-medium">This document can't be embedded</p>
        <p className="text-xs text-slate-500 mb-4 max-w-sm mx-auto">
          Due to Google sharing settings, this document can't be previewed here. 
          You can still open it in Google Docs.
        </p>
        <div className="flex justify-center gap-2">
          <Button onClick={() => window.open(doc.url, '_blank')}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Google Docs
          </Button>
          <Button variant="outline" onClick={() => navigator.clipboard.writeText(doc.url).then(() => toast.success('Copied!'))}>
            <Copy className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
        </div>
        
        {/* Help section */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="link" size="sm" className="mt-4 text-xs text-slate-500">
              <HelpCircle className="w-3 h-3 mr-1" />
              How to enable preview
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 text-left">
            <h4 className="font-medium text-sm mb-2">Enable Document Preview</h4>
            <p className="text-xs text-slate-600 mb-2">
              To preview documents here, your Google Doc needs appropriate sharing settings:
            </p>
            <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
              <li>Open the document in Google Docs</li>
              <li>Click "Share" in the top right</li>
              <li>Under "General access", select "Anyone with the link"</li>
              <li>Set the access level to "Viewer"</li>
            </ol>
            <p className="text-xs text-slate-500 mt-2 italic">
              Note: Only enable this if you're comfortable with the sharing settings.
            </p>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
          <div className="animate-pulse text-sm text-slate-500">Loading preview...</div>
        </div>
      )}
      <iframe
        src={embedUrl}
        className="w-full border-0"
        style={{ height: '500px' }}
        onLoad={() => setIsLoading(false)}
        onError={() => { setEmbedError(true); setIsLoading(false); }}
        title={`Preview: ${doc.title || 'Document'}`}
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
};

export default GoogleDocsPanel;
